import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, NavLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  FaBars,
  FaTimes,
  FaFacebookF,
  FaInstagram,
  FaYoutube,
  FaPinterestP,
  FaPhone,
  FaMapMarkerAlt,
  FaSearch,
  FaShoppingCart,
  FaCalendarAlt,
  FaUser,
  FaChevronDown,
  FaLeaf,
} from 'react-icons/fa';
import { useCart } from '../../context/CartContext';
import { useSubscriptionCart } from '../../context/SubscriptionCartContext';
import { SiGoogleplay, SiAppstore } from 'react-icons/si';
import logo from '../../assets/images/logo.png';
import './Navbar.css';
import { AccountDropdownMenu } from './AccountDropdownMenu';
import KitchenClosedBanner from '../KitchenClosedBanner/KitchenClosedBanner';
import PasswordField from '../PasswordField/PasswordField';
import { API_BASE_URL } from '../../config/api';
import { INDIAN_STATES } from '../../data/indianStates';
import { formatPhoneForRegister, formatPhoneForDisplay, sanitizeLocalMobileInput, sanitizePhoneDigitsInput, validateLocalMobile, validatePhoneDigitsValue, getLocalMobileLength } from '../../utils/phone';
import { resolveLoginUsername } from '../../utils/authLoginUtils';
import { performWebLogoutWithApi } from '../../pages/Account/authRecoveryApi';
import {
  profileSaveErrorMessage as apiProfileSaveErrorMessage,
  formatDateOfBirthForCreateProfileApi,
  AUTH_REDIRECT_QUERY_PARAM,
  FORCE_REAUTH_QUERY_PARAM,
  isUsablePostLoginRedirectPath,
  LOGIN_QUERY_PARAM,
  notifyAuthUpdated,
  POST_LOGIN_REDIRECT_STORAGE_KEY,
  readStoredAuth,
} from '../../pages/Account/profileFormUtils';

const AUTH_STORAGE_KEY = 'digirasa_web_auth';
const LOGIN_ROLE_ID = 3;
const COUNTRY_CODES = ['91', '1', '44', '61', '65', '971'];
const OTP_VALID_SEC = 6 * 60;
const OTP_RESEND_COOLDOWN_SEC = 30;

const INITIAL_PROFILE = {
  first_name: '',
  last_name: '',
  gender: '',
  city: '',
  state: '',
  country: 'India',
  pincode: '',
  date_of_birth: '',
  guardian_name: '',
  guardian_email: '',
  guardian_mobile: '',
};

const SIGNUP_PROFILE_FIELD_IDS = {
  first_name: 'web-profile-fn',
  last_name: 'web-profile-ln',
  gender: 'web-profile-gender',
  date_of_birth: 'web-profile-dob-day',
  country: 'web-profile-country',
  state: 'web-profile-state',
  city: 'web-profile-city',
  pincode: 'web-profile-pin',
  guardian_name: 'web-profile-g-name',
  guardian_email: 'web-profile-g-email',
  guardian_mobile: 'web-profile-g-mobile',
};

/** Form display order — first invalid field is focused after Save & continue. */
const SIGNUP_PROFILE_FIELD_ORDER = [
  'first_name',
  'last_name',
  'gender',
  'date_of_birth',
  'city',
  'state',
  'country',
  'pincode',
  'guardian_name',
  'guardian_email',
  'guardian_mobile',
];

const SIGNUP_CREDENTIALS_FIELD_IDS = {
  username: 'web-signup-username',
  email: 'web-signup-email',
  password: 'web-signup-password',
  confirmPassword: 'web-signup-confirm-password',
};

const SIGNUP_CREDENTIALS_FIELD_ORDER = ['username', 'email', 'password', 'confirmPassword'];

function scrollSignupFieldIntoViewAndFocus(fieldEl) {
  if (!fieldEl || !(fieldEl instanceof HTMLElement)) return;
  const group = fieldEl.closest('.auth-form__group') || fieldEl.closest('.auth-form__fieldset');
  const scrollTarget = group || fieldEl;
  scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });

  const applyFocus = () => {
    try {
      fieldEl.focus({ preventScroll: true });
    } catch {
      try {
        fieldEl.focus();
      } catch {
        /* ignore */
      }
    }
  };

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(applyFocus);
  });
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validateSignupPassword = (pw) => {
  const s = String(pw || '');
  if (s.length < 8) return 'Password must be at least 8 characters.';
  if (/\s/.test(s)) return 'Password must not contain spaces.';
  if (!/[a-zA-Z]/.test(s)) return 'Password must include letters.';
  if (!/[0-9]/.test(s)) return 'Password must include numbers.';
  if (!/[^a-zA-Z0-9]/.test(s)) return 'Password must include a special character.';
  return '';
};

const validateUsername = (u) => {
  const s = String(u || '').trim();
  if (s.length < 3) return 'Username must be at least 3 characters.';
  if (!/^[a-zA-Z0-9_]+$/.test(s)) return 'Username may only use letters, numbers, and underscores.';
  return '';
};

const getValueByPath = (obj, path) =>
  path.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
const getFirstValue = (obj, paths) => {
  for (const path of paths) {
    const value = getValueByPath(obj, path);
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return '';
};

const parseResponseJson = async (res) => {
  const text = await res.text();
  if (!text?.trim()) return {};
  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
};

const registerErrorMessage = (status, data) => {
  const fromBody = getFirstValue(data, ['detail', 'message', 'error', 'data.message']);
  if (fromBody) return fromBody;
  if (status === 409) return 'This account already exists. Please sign in instead.';
  if (status === 400)
    return 'Invalid registration details. Please check your information and try again.';
  if (status === 404) return 'Registration service not found. Please try again later.';
  if (status >= 500) return 'Server error. Please try again.';
  return 'Registration failed. Please try again.';
};

const verifyOtpErrorMessage = (status, data) => {
  const fromBody = getFirstValue(data, ['detail', 'message', 'error']);
  if (fromBody) return fromBody;
  if (status === 400) return 'Invalid or expired OTP.';
  if (status === 409) return 'This phone number is already registered.';
  if (status === 404) return 'Verification not found. Please resend OTP.';
  return 'Could not verify OTP.';
};

/** APIs may omit `verified` and only return message; or use success / PascalCase. */
const isOtpVerifyBodySuccessful = (data) => {
  const flag = getFirstValue(data, [
    'verified',
    'Verified',
    'data.verified',
    'success',
    'data.success',
    'data.Verified',
  ]);
  if (flag === true || flag === 'true' || flag === 1 || flag === '1') return true;
  if (flag === false || flag === 'false' || flag === 0 || flag === '0') return false;
  return null;
};

const ageFromIsoDateInput = (iso) => {
  if (!iso?.trim()) return null;
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const birth = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const md = today.getMonth() - birth.getMonth();
  if (md < 0 || (md === 0 && today.getDate() < birth.getDate())) age -= 1;
  return age;
};

const MONTH_OPTIONS = [
  { value: '01', label: '01 - Jan' },
  { value: '02', label: '02 - Feb' },
  { value: '03', label: '03 - Mar' },
  { value: '04', label: '04 - Apr' },
  { value: '05', label: '05 - May' },
  { value: '06', label: '06 - Jun' },
  { value: '07', label: '07 - Jul' },
  { value: '08', label: '08 - Aug' },
  { value: '09', label: '09 - Sep' },
  { value: '10', label: '10 - Oct' },
  { value: '11', label: '11 - Nov' },
  { value: '12', label: '12 - Dec' },
];

const DOB_YEAR_START = 1920;

const daysInMonth = (year, month) => {
  const y = Number(year);
  const mo = Number(month);
  if (!y || !mo) return 31;
  return new Date(y, mo, 0).getDate();
};

const buildIsoDob = (day, month, year) => {
  if (!day || !month || !year) return '';
  const d = Number(day);
  const mo = Number(month);
  const y = Number(year);
  if (!d || !mo || !y) return '';
  const maxDay = daysInMonth(y, mo);
  if (d < 1 || d > maxDay) return '';
  const iso = `${String(y).padStart(4, '0')}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  return ageFromIsoDateInput(iso) == null ? '' : iso;
};

const buildDobYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = currentYear; y >= DOB_YEAR_START; y -= 1) {
    years.push(String(y));
  }
  return years;
};

const DOB_YEAR_OPTIONS = buildDobYearOptions();

/** 2–50 chars, alphabetic words (spaces / hyphen / apostrophe between parts). */
const isProfilePersonName = (s) => {
  const t = s.trim();
  if (t.length < 2 || t.length > 50) return false;
  return /^[A-Za-z]+(?:[ \-'][A-Za-z]+)*$/.test(t);
};

const formatCountdown = (totalSec) => {
  const s = Math.max(0, totalSec);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
};

const getVerificationIdFromPayload = (data) => {
  const raw = getFirstValue(data, [
    'verification_id',
    'verificationId',
    'verificationID',
    'VerificationId',
    'data.verification_id',
    'data.verificationId',
    'data.verificationID',
    'result.verification_id',
    'result.verificationId',
    'body.verification_id',
    'body.verificationId',
    'payload.verification_id',
    'payload.verificationId',
    'response.verification_id',
    'response.verificationId',
    'data.data.verification_id',
    'data.data.verificationId',
  ]);
  if (raw === '' || raw === undefined || raw === null) return '';
  return String(raw).trim();
};

/** Some deployments return only { message } and key OTP by phone; headers may carry id if CORS exposes them. */
const getVerificationIdFromHeaders = (res) => {
  const names = ['x-verification-id', 'verification-id', 'x-verification_id', 'verification_id'];
  for (const n of names) {
    const v = res.headers.get(n);
    if (v != null && String(v).trim() !== '') return String(v).trim();
  }
  return '';
};

const resolveVerificationIdAfterSendOtp = (res, data, phoneDigitsOnly) => {
  const fromBody = getVerificationIdFromPayload(data);
  if (fromBody) return fromBody;
  const fromHeader = getVerificationIdFromHeaders(res);
  if (fromHeader) return fromHeader;
  const phone = String(phoneDigitsOnly ?? '').trim();
  if (phone) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.info(
        '[Digirasa] sendOTP: no verification_id in body/headers; using phone for verifyOTP path:',
        phone,
      );
    }
    return phone;
  }
  return '';
};

// Navigation items with dropdown support
const navItems = [
  { label: 'HOME', path: '/', hasDropdown: false },
  {
    label: 'ABOUT',
    hasDropdown: true,
    dropdown: [
      { path: '/about-us', label: 'About Us' },
      { path: '/whychooseus', label: 'Why Choose Us' },
      { path: '/reviews', label: 'Reviews' },
      { path: '/services', label: 'Services' },
      { path: '/gallery', label: 'Gallery' },
    ],
  },
  { label: 'OUR MENU', path: '/our-menu', hasDropdown: false },
  { label: 'MEAL PLANS', path: '/meal-plans', hasDropdown: false },
  { label: 'BULK ORDER', path: '/bulk-order', hasDropdown: false },
  {
    label: 'SUPPORT',
    hasDropdown: true,
    dropdown: [
      { path: '/contact', label: 'Contact' },
      { path: '/faq', label: 'FAQ' },
    ],
  },
];

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [loginIdentifierMode, setLoginIdentifierMode] = useState('phone');
  const [authForm, setAuthForm] = useState({
    countryCode: '91',
    mobile: '',
    password: '',
    confirmPassword: '',
    username: '',
    email: '',
    loginAccount: '',
  });
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [authSuccess, setAuthSuccess] = useState('');
  const [authToken, setAuthToken] = useState('');
  const [authUser, setAuthUser] = useState(null);
  /** Signup wizard: phone → otp → credentials → profile (optional) → success */
  const [signupStep, setSignupStep] = useState('phone');
  const [signupVerificationId, setSignupVerificationId] = useState('');
  const [signupOtp, setSignupOtp] = useState('');
  const [otpSecondsLeft, setOtpSecondsLeft] = useState(0);
  const [resendCooldownSec, setResendCooldownSec] = useState(0);
  const [pendingSignup, setPendingSignup] = useState(null);
  const [profileForm, setProfileForm] = useState(() => ({ ...INITIAL_PROFILE }));
  const [dobParts, setDobParts] = useState({ day: '', month: '', year: '' });
  const [profileFieldErrors, setProfileFieldErrors] = useState({});
  const [profileErrorFocusKey, setProfileErrorFocusKey] = useState(null);
  const [credentialsFieldErrors, setCredentialsFieldErrors] = useState({});
  const [credentialsErrorFocusKey, setCredentialsErrorFocusKey] = useState(null);
  const [hideSignupEmailVerifyBanner, setHideSignupEmailVerifyBanner] = useState(false);
  const [signupSession, setSignupSession] = useState(null);
  /** @type {null | 'topbar' | 'navbar'} */
  const [accountDropdown, setAccountDropdown] = useState(null);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  const [supportDropdownOpen, setSupportDropdownOpen] = useState(false);
  const aboutRef = useRef(null);
  const supportRef = useRef(null);
  const topbarAccountRef = useRef(null);
  const navbarAccountRef = useRef(null);
  const searchRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { cartCount } = useCart();
  const { subscriptionCartCount } = useSubscriptionCart();

  const resetLoginIdentifier = useCallback(() => {
    setLoginIdentifierMode('phone');
  }, []);

  const resetSignupWizard = useCallback(() => {
    setSignupStep('phone');
    setSignupVerificationId('');
    setSignupOtp('');
    setOtpSecondsLeft(0);
    setResendCooldownSec(0);
    setPendingSignup(null);
    setProfileForm({ ...INITIAL_PROFILE });
    setDobParts({ day: '', month: '', year: '' });
    setProfileFieldErrors({});
    setProfileErrorFocusKey(null);
    setCredentialsFieldErrors({});
    setCredentialsErrorFocusKey(null);
    setHideSignupEmailVerifyBanner(false);
    setSignupSession(null);
  }, []);

  useEffect(() => {
    if (!authOpen) {
      resetSignupWizard();
      resetLoginIdentifier();
    }
  }, [authOpen, resetSignupWizard, resetLoginIdentifier]);

  useEffect(() => {
    if (signupStep !== 'otp' || authMode !== 'signup') return undefined;
    const id = window.setInterval(() => {
      setOtpSecondsLeft((t) => (t > 0 ? t - 1 : 0));
      setResendCooldownSec((t) => (t > 0 ? t - 1 : 0));
    }, 1000);
    return () => window.clearInterval(id);
  }, [signupStep, authMode]);

  useEffect(() => {
    if (!profileErrorFocusKey || !profileFieldErrors[profileErrorFocusKey]) return undefined;
    const fieldId = SIGNUP_PROFILE_FIELD_IDS[profileErrorFocusKey];
    if (!fieldId) return undefined;
    const fieldEl = document.getElementById(fieldId);
    if (!fieldEl) return undefined;
    scrollSignupFieldIntoViewAndFocus(fieldEl);
    return undefined;
  }, [profileErrorFocusKey, profileFieldErrors]);

  useEffect(() => {
    if (!credentialsErrorFocusKey || !credentialsFieldErrors[credentialsErrorFocusKey]) {
      return undefined;
    }
    const fieldId = SIGNUP_CREDENTIALS_FIELD_IDS[credentialsErrorFocusKey];
    if (!fieldId) return undefined;
    const fieldEl = document.getElementById(fieldId);
    if (!fieldEl) return undefined;
    scrollSignupFieldIntoViewAndFocus(fieldEl);
    return undefined;
  }, [credentialsErrorFocusKey, credentialsFieldErrors]);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrolled(window.scrollY > 20);
          ticking = false;
        });
        ticking = true;
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (searchOpen && searchRef.current) searchRef.current.focus();
  }, [searchOpen]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (aboutRef.current && !aboutRef.current.contains(e.target)) {
        setAboutDropdownOpen(false);
      }
      if (supportRef.current && !supportRef.current.contains(e.target)) {
        setSupportDropdownOpen(false);
      }
      const inTopbar = topbarAccountRef.current?.contains(e.target);
      const inNavbar = navbarAccountRef.current?.contains(e.target);
      if (!inTopbar && !inNavbar) {
        setAccountDropdown(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setSearchOpen(false);
        setAccountDropdown(null);
        setMenuOpen(false);
        setAuthOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(AUTH_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.token) {
        setAuthToken(parsed.token);
        setAuthUser(parsed.user || null);
      }
    } catch {
      localStorage.removeItem(AUTH_STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    const sync = () => {
      try {
        const raw = localStorage.getItem(AUTH_STORAGE_KEY);
        if (!raw) {
          setAuthToken('');
          setAuthUser(null);
          return;
        }
        const parsed = JSON.parse(raw);
        if (parsed?.token) {
          setAuthToken(parsed.token);
          setAuthUser(parsed.user || null);
        }
      } catch {
        setAuthToken('');
        setAuthUser(null);
      }
    };
    window.addEventListener('digirasa-auth-updated', sync);
    return () => window.removeEventListener('digirasa-auth-updated', sync);
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/our-menu?search=${encodeURIComponent(searchQuery.trim())}`;
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  const openAuthModal = () => {
    setAuthMode('login');
    resetLoginIdentifier();
    setAuthError('');
    setAuthSuccess('');
    setAccountDropdown(null);
    setMenuOpen(false);
    setAuthOpen(true);
  };

  useEffect(() => {
    if (searchParams.get(LOGIN_QUERY_PARAM) !== '1') return;

    const redirectTarget = searchParams.get(AUTH_REDIRECT_QUERY_PARAM);
    const forceReauth = searchParams.get(FORCE_REAUTH_QUERY_PARAM) === '1';

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete(LOGIN_QUERY_PARAM);
    nextParams.delete(AUTH_REDIRECT_QUERY_PARAM);
    nextParams.delete(FORCE_REAUTH_QUERY_PARAM);
    const remaining = nextParams.toString();
    setSearchParams(remaining ? nextParams : {}, { replace: true });

    if (forceReauth) {
      localStorage.removeItem(AUTH_STORAGE_KEY);
      setAuthToken('');
      setAuthUser(null);
    } else {
      const hasSession = Boolean(authToken || readStoredAuth().token);
      if (hasSession) {
        if (isUsablePostLoginRedirectPath(redirectTarget)) {
          navigate(redirectTarget.trim(), { replace: true });
        }
        return;
      }
    }

    if (isUsablePostLoginRedirectPath(redirectTarget)) {
      try {
        sessionStorage.setItem(POST_LOGIN_REDIRECT_STORAGE_KEY, redirectTarget.trim());
      } catch {
        /* ignore */
      }
    } else {
      try {
        sessionStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
      } catch {
        /* ignore */
      }
    }

    setAuthMode('login');
    resetLoginIdentifier();
    setAuthError('');
    setAuthSuccess('');
    setAccountDropdown(null);
    setAuthOpen(true);
  }, [authToken, navigate, resetLoginIdentifier, searchParams, setSearchParams]);

  const handleAuthInput = (e) => {
    const { name, value } = e.target;
    if (name === 'mobile') {
      setAuthForm((prev) => ({
        ...prev,
        mobile: sanitizeLocalMobileInput(value, prev.countryCode),
      }));
      return;
    }
    if (name === 'countryCode') {
      setAuthForm((prev) => ({
        ...prev,
        countryCode: value,
        mobile: sanitizeLocalMobileInput(prev.mobile, value),
      }));
      return;
    }
    setAuthForm((prev) => ({ ...prev, [name]: value }));
    if (
      name === 'username'
      || name === 'email'
      || name === 'password'
      || name === 'confirmPassword'
    ) {
      setCredentialsFieldErrors((prev) => {
        if (!prev[name]) return prev;
        const next = { ...prev };
        delete next[name];
        return next;
      });
      setCredentialsErrorFocusKey((prev) => (prev === name ? null : prev));
    }
  };

  const handleLogout = async () => {
    try {
      await performWebLogoutWithApi();
    } finally {
      setAuthToken('');
      setAuthUser(null);
      setAuthForm((prev) => ({
        ...prev,
        mobile: '',
        password: '',
        confirmPassword: '',
        username: '',
        email: '',
        loginAccount: '',
      }));
      setAuthError('');
      setAuthSuccess('');
      setAccountDropdown(null);
      setAuthOpen(false);
      navigate('/', { replace: true });
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthSuccess('');

    // Validate identifier first (mobile vs username/email), then password.
    const resolved = resolveLoginUsername({
      mode: loginIdentifierMode,
      countryCode: authForm.countryCode,
      mobile: authForm.mobile,
      accountIdentifier: authForm.loginAccount,
    });
    if ('error' in resolved) {
      setAuthError(resolved.error);
      return;
    }

    const password = String(authForm.password ?? '').trim();
    if (!password) {
      setAuthError('Please enter your password.');
      return;
    }

    const { username } = resolved;

    setAuthLoading(true);
    try {
      const body = new URLSearchParams({
        username,
        password,
        role: String(LOGIN_ROLE_ID),
      });

      const res = await fetch(`${API_BASE_URL}/auth/login/v1`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg =
          getFirstValue(data, ['detail', 'message', 'error', 'data.message']) ||
          'Login failed. Please check your credentials.';
        throw new Error(msg);
      }

      const token = getFirstValue(data, [
        'access_token',
        'data.token',
        'token',
        'accessToken',
        'data.accessToken',
      ]);
      if (!token) {
        throw new Error('Login succeeded but token is missing in response.');
      }

      const refreshToken = getFirstValue(data, ['refresh_token', 'data.refresh_token']) || '';
      const user = {
        id: getFirstValue(data, ['data.id', 'data.user.id', 'user.id', 'id']) || undefined,
        name:
          getFirstValue(data, [
            'data.firstName',
            'data.first_name',
            'data.username',
            'data.user.name',
            'data.name',
            'user.name',
            'name',
            'fullName',
          ]) || 'My Account',
        email: getFirstValue(data, ['data.email', 'user.email', 'email']) || '',
        mobile:
          getFirstValue(data, [
            'data.phone_number',
            'data.user.mobile',
            'data.mobile',
            'user.mobile',
            'mobile',
          ]) || username,
        username:
          getFirstValue(data, [
            'data.username',
            'data.user.username',
            'user.username',
            'username',
          ]) || '',
      };

      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({ token, refreshToken, user }));
      setAuthToken(token);
      setAuthUser(user);
      setAuthSuccess('Login successful.');
      setAuthForm((prev) => ({ ...prev, mobile: '', password: '', confirmPassword: '', loginAccount: '' }));
      setAccountDropdown(null);
      setAuthOpen(false);
      notifyAuthUpdated();

      let postLoginPath = '';
      try {
        postLoginPath = sessionStorage.getItem(POST_LOGIN_REDIRECT_STORAGE_KEY) || '';
        sessionStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      if (isUsablePostLoginRedirectPath(postLoginPath)) {
        navigate(postLoginPath, { replace: true });
      }
    } catch (err) {
      setAuthError(err.message || 'Unable to login right now.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleProfileInput = (e) => {
    const { name, value } = e.target;
    if (name === 'guardian_mobile') {
      setProfileForm((prev) => ({
        ...prev,
        guardian_mobile: sanitizePhoneDigitsInput(value, 15),
      }));
    } else {
      setProfileForm((prev) => ({ ...prev, [name]: value }));
    }
    setProfileFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
    setProfileErrorFocusKey((prev) => (prev === name ? null : prev));
  };

  const handleDobPartChange = (part, value) => {
    setDobParts((prev) => {
      const next = {
        day: part === 'day' ? value : prev.day,
        month: part === 'month' ? value : prev.month,
        year: part === 'year' ? value : prev.year,
      };
      if ((part === 'month' || part === 'year') && next.day && next.month && next.year) {
        const maxDay = daysInMonth(next.year, next.month);
        if (Number(next.day) > maxDay) {
          next.day = String(maxDay).padStart(2, '0');
        }
      }
      const iso = buildIsoDob(next.day, next.month, next.year);
      setProfileForm((profilePrev) => ({ ...profilePrev, date_of_birth: iso }));
      return next;
    });
    setProfileFieldErrors((prev) => {
      if (!prev.date_of_birth) return prev;
      const next = { ...prev };
      delete next.date_of_birth;
      return next;
    });
    setProfileErrorFocusKey((prev) => (prev === 'date_of_birth' ? null : prev));
  };

  const handleSignupPhoneContinue = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthSuccess('');

    const countryCode = String(authForm.countryCode ?? '91').trim();
    const mobile = String(authForm.mobile ?? '').trim();
    const phoneCheck = validateLocalMobile(mobile, countryCode);
    if (!phoneCheck.ok) {
      setAuthError(phoneCheck.message);
      return;
    }
    const phone_number = formatPhoneForRegister(countryCode, phoneCheck.digits);

    setAuthLoading(true);
    try {
      const url = `${API_BASE_URL}/auth/sendOTP/register/${encodeURIComponent(phone_number)}`;
      const res = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' } });
      const data = await parseResponseJson(res);
      if (!res.ok) {
        const msg =
          getFirstValue(data, ['detail', 'message', 'error']) ||
          (res.status === 400 ? 'Invalid phone number format.' : 'Failed to send OTP.');
        throw new Error(msg);
      }
      const vid = resolveVerificationIdAfterSendOtp(res, data, phone_number);
      if (!vid) {
        throw new Error('Missing verification id from server.');
      }
      setPendingSignup({ phone_number });
      setSignupVerificationId(vid);
      setSignupStep('otp');
      setSignupOtp('');
      setOtpSecondsLeft(OTP_VALID_SEC);
      setResendCooldownSec(OTP_RESEND_COOLDOWN_SEC);
      setAuthSuccess(getFirstValue(data, ['message', 'data.message']) || 'OTP sent successfully.');
    } catch (err) {
      setAuthError(err.message || 'Failed to send OTP.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSignupResendOtp = async () => {
    if (resendCooldownSec > 0 || authLoading) return;
    setAuthError('');
    setAuthSuccess('');
    const phoneForOtp =
      pendingSignup?.phone_number ||
      formatPhoneForRegister(authForm.countryCode, authForm.mobile);
    if (!phoneForOtp) {
      setAuthError('Please enter your mobile number again.');
      setSignupStep('phone');
      return;
    }
    setAuthLoading(true);
    try {
      const url = `${API_BASE_URL}/auth/sendOTP/register/${encodeURIComponent(phoneForOtp)}`;
      const res = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' } });
      const data = await parseResponseJson(res);
      if (!res.ok) {
        const msg =
          getFirstValue(data, ['detail', 'message', 'error']) ||
          (res.status === 400 ? 'Invalid phone number format.' : 'Failed to send OTP.');
        throw new Error(msg);
      }
      const vid = resolveVerificationIdAfterSendOtp(res, data, phoneForOtp);
      if (!vid) {
        throw new Error('Missing verification id from server.');
      }
      setPendingSignup((prev) => ({ ...(prev || {}), phone_number: phoneForOtp }));
      setSignupVerificationId(vid);
      setOtpSecondsLeft(OTP_VALID_SEC);
      setResendCooldownSec(OTP_RESEND_COOLDOWN_SEC);
      setAuthSuccess(getFirstValue(data, ['message', 'data.message']) || 'OTP sent again.');
    } catch (err) {
      setAuthError(err.message || 'Failed to send OTP.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSignupOtpSubmit = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthSuccess('');

    const otp = signupOtp.replace(/\D/g, '');
    if (otp.length !== 6) {
      setAuthError('Please enter the 6-digit OTP.');
      return;
    }
    if (!signupVerificationId) {
      setAuthError('Please request OTP again.');
      return;
    }
    if (!pendingSignup?.phone_number) {
      setAuthError('Session expired. Please start sign up again.');
      setSignupStep('phone');
      return;
    }

    setAuthLoading(true);
    try {
      const vUrl = `${API_BASE_URL}/auth/verifyOTP/${encodeURIComponent(otp)}/${encodeURIComponent(signupVerificationId)}`;
      const vRes = await fetch(vUrl, { method: 'GET', headers: { Accept: 'application/json' } });
      const vData = await parseResponseJson(vRes);
      if (!vRes.ok) {
        throw new Error(verifyOtpErrorMessage(vRes.status, vData));
      }
      const otpOk = isOtpVerifyBodySuccessful(vData);
      if (otpOk === false) {
        throw new Error(
          getFirstValue(vData, ['message', 'detail', 'error']) || 'OTP verification failed.',
        );
      }

      setSignupStep('credentials');
      setSignupOtp('');
      setAuthSuccess('Mobile verified. Create your account details next.');
    } catch (err) {
      setAuthError(err.message || 'Something went wrong.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSignupCredentialsSubmit = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthSuccess('');
    setCredentialsFieldErrors({});
    setCredentialsErrorFocusKey(null);

    const username = String(authForm.username ?? '').trim();
    const email = String(authForm.email ?? '').trim();
    const password = String(authForm.password ?? '').trim();
    const confirmPassword = String(authForm.confirmPassword ?? '').trim();
    const phone_number = pendingSignup?.phone_number;

    if (!phone_number) {
      setAuthError('Session expired. Please start sign up again.');
      setSignupStep('phone');
      return;
    }

    /** @type {Record<string, string>} */
    const nextFieldErrors = {};
    const userMsg = validateUsername(username);
    if (userMsg) {
      nextFieldErrors.username = userMsg;
    }
    if (!EMAIL_RE.test(email)) {
      nextFieldErrors.email = 'Please enter a valid email address.';
    }
    const pwMsg = validateSignupPassword(password);
    if (pwMsg) {
      nextFieldErrors.password = pwMsg;
    }
    if (!confirmPassword) {
      nextFieldErrors.confirmPassword = 'Please confirm your password.';
    } else if (password && password !== confirmPassword) {
      nextFieldErrors.confirmPassword = 'Passwords do not match.';
    }

    const firstErrorKey = SIGNUP_CREDENTIALS_FIELD_ORDER.find((key) => nextFieldErrors[key]);
    if (firstErrorKey) {
      setCredentialsFieldErrors(nextFieldErrors);
      setCredentialsErrorFocusKey(firstErrorKey);
      return;
    }

    setAuthLoading(true);
    try {
      const regRes = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roleID: LOGIN_ROLE_ID,
          username,
          email,
          password,
          phone_number,
        }),
      });
      const regData = await parseResponseJson(regRes);
      if (!regRes.ok) {
        throw new Error(registerErrorMessage(regRes.status, regData));
      }

      const registeredUsername =
        getFirstValue(regData, ['data.username', 'username']) || username;
      const registeredEmail = getFirstValue(regData, ['data.email', 'email']) || email;
      const registeredMobile =
        getFirstValue(regData, ['data.phone_number', 'phone_number']) || phone_number;

      // Keep register token only for optional profile step — do not persist as signed-in session.
      const token = getFirstValue(regData, [
        'access_token',
        'data.token',
        'token',
        'accessToken',
        'data.accessToken',
        'data.access_token',
      ]);
      const userId = getFirstValue(regData, [
        'data.id',
        'data.user.id',
        'user.id',
        'id',
        'data.userId',
        'userId',
      ]);

      setPendingSignup({
        phone_number: registeredMobile,
        username: registeredUsername,
        email: registeredEmail,
      });
      setAuthForm((prev) => ({ ...prev, password: '', confirmPassword: '' }));
      setProfileForm({ ...INITIAL_PROFILE });
      setDobParts({ day: '', month: '', year: '' });
      setCredentialsFieldErrors({});
      setCredentialsErrorFocusKey(null);

      if (token && userId != null && String(userId).trim() !== '') {
        setSignupSession({
          token: String(token),
          user: { id: userId },
        });
      } else {
        setSignupSession(null);
      }
      setHideSignupEmailVerifyBanner(false);
      setSignupStep('profile');
      setAuthSuccess('');
    } catch (err) {
      setAuthError(err.message || 'Something went wrong.');
    } finally {
      setAuthLoading(false);
    }
  };

  const skipSignupProfile = () => {
    setAuthError('');
    setAuthSuccess('');
    setSignupSession(null);
    setProfileForm({ ...INITIAL_PROFILE });
    setDobParts({ day: '', month: '', year: '' });
    setSignupStep('success');
  };

  const goToLoginAfterSignup = () => {
    const username = pendingSignup?.username || String(authForm.username || '').trim();
    const phoneDigits = String(pendingSignup?.phone_number || '').replace(/\D/g, '');
    const countryCode = String(authForm.countryCode || '91').trim() || '91';
    const localLen = getLocalMobileLength(countryCode);
    let localMobile = '';
    if (phoneDigits.length >= localLen) {
      localMobile = phoneDigits.slice(-localLen);
    }

    setAuthMode('login');
    resetLoginIdentifier();
    setAuthForm((prev) => ({
      ...prev,
      password: '',
      confirmPassword: '',
      username: username || prev.username,
      loginAccount: username || '',
      countryCode,
      mobile: localMobile || prev.mobile,
    }));
    setSignupSession(null);
    setSignupStep('phone');
    setSignupVerificationId('');
    setSignupOtp('');
    setOtpSecondsLeft(0);
    setResendCooldownSec(0);
    setPendingSignup(null);
    setProfileForm({ ...INITIAL_PROFILE });
    setDobParts({ day: '', month: '', year: '' });
    setAuthError('');
    setAuthSuccess('Registration successful. Please sign in with your new account.');
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthSuccess('');
    setProfileFieldErrors({});
    setProfileErrorFocusKey(null);

    if (!signupSession?.token || !pendingSignup) {
      setHideSignupEmailVerifyBanner(true);
      setAuthError(
        'Account was created, but profile could not be saved in this session. Skip for now and complete your profile after sign in.',
      );
      return;
    }

    const fn = profileForm.first_name.trim();
    const ln = profileForm.last_name.trim();
    const cityTrim = profileForm.city.trim();
    /** @type {Record<string, string>} */
    const nextFieldErrors = {};

    if (!isProfilePersonName(fn)) {
      nextFieldErrors.first_name =
        'First name must be 2–50 characters (letters only, with spaces or hyphens between words if needed).';
    }
    if (!isProfilePersonName(ln)) {
      nextFieldErrors.last_name =
        'Last name must be 2–50 characters (letters only, with spaces or hyphens between words if needed).';
    }
    if (!profileForm.gender) {
      nextFieldErrors.gender = 'Please select gender.';
    }

    let age = null;
    if (!dobParts.day || !dobParts.month || !dobParts.year) {
      nextFieldErrors.date_of_birth = 'Please select day, month, and year (DD/MM/YYYY).';
    } else if (!profileForm.date_of_birth) {
      nextFieldErrors.date_of_birth = 'Please enter a valid date of birth.';
    } else {
      age = ageFromIsoDateInput(profileForm.date_of_birth);
      if (age == null) {
        nextFieldErrors.date_of_birth = 'Please enter a valid date of birth.';
      }
    }

    if (!profileForm.state) {
      nextFieldErrors.state = 'Please select state.';
    }
    if (cityTrim.length < 2 || cityTrim.length > 50) {
      nextFieldErrors.city = 'City must be 2–50 characters.';
    }
    if (!/^\d{6}$/.test(profileForm.pincode.trim())) {
      nextFieldErrors.pincode = 'Pincode must be 6 digits.';
    }

    const gName = profileForm.guardian_name.trim();
    const gEmail = profileForm.guardian_email.trim();
    const guardianRequired = age != null && age < 18;
    const gMobCheck = validatePhoneDigitsValue(profileForm.guardian_mobile, {
      optional: !guardianRequired,
    });
    if (guardianRequired) {
      if (!isProfilePersonName(gName)) {
        nextFieldErrors.guardian_name = 'Guardian name is required for users under 18.';
      }
      if (!EMAIL_RE.test(gEmail)) {
        nextFieldErrors.guardian_email = 'Guardian email is required for users under 18.';
      }
      if (!gMobCheck.ok) {
        nextFieldErrors.guardian_mobile =
          gMobCheck.message === 'Please enter a valid mobile number.'
            ? 'Guardian mobile is required for users under 18.'
            : gMobCheck.message;
      }
    } else {
      if (gName && !isProfilePersonName(gName)) {
        nextFieldErrors.guardian_name =
          'Guardian name must be 2–50 characters (letters, same rules as your name), or leave blank.';
      }
      if (gEmail && !EMAIL_RE.test(gEmail)) {
        nextFieldErrors.guardian_email = 'Please enter a valid guardian email, or leave it blank.';
      }
      if (!gMobCheck.ok) {
        nextFieldErrors.guardian_mobile = gMobCheck.message;
      }
    }

    const firstErrorKey = SIGNUP_PROFILE_FIELD_ORDER.find((key) => nextFieldErrors[key]);
    if (firstErrorKey) {
      setProfileFieldErrors(nextFieldErrors);
      setProfileErrorFocusKey(firstErrorKey);
      setHideSignupEmailVerifyBanner(true);
      return;
    }

    const gMobDigits = gMobCheck.digits || '';

    const userIdNum = Number(signupSession.user.id);
    if (!Number.isFinite(userIdNum)) {
      setAuthError('Invalid account. Please sign up again.');
      return;
    }

    setAuthLoading(true);
    try {
      const p = pendingSignup;
      const body = {
        id: userIdNum,
        username: p.username,
        email: p.email,
        phone_number: p.phone_number,
        firstName: fn,
        lastName: ln,
        gender: profileForm.gender,
        country: (profileForm.country || 'India').trim() || 'India',
        state: profileForm.state,
        city: cityTrim,
        pincode: profileForm.pincode.trim(),
      };
      const dobApi = formatDateOfBirthForCreateProfileApi(profileForm.date_of_birth);
      if (dobApi) body.dateOfBirth = dobApi;
      if (gName) body.guardianName = gName;
      if (gEmail) body.guardianEmail = gEmail;
      if (gMobDigits) body.guardianMobile = gMobDigits;

      const res = await fetch(`${API_BASE_URL}/auth/createProfile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${signupSession.token}`,
        },
        body: JSON.stringify(body),
      });
      const data = await parseResponseJson(res);
      if (!res.ok) {
        throw new Error(apiProfileSaveErrorMessage(res.status, data));
      }

      // Profile saved during signup — do not auto sign-in; show confirmation instead.
      setSignupSession(null);
      setPendingSignup({
        phone_number: data.phone_number || p.phone_number,
        username:
          getFirstValue(data, ['username', 'userName']) ||
          p.username ||
          '',
        email: data.email || p.email,
      });
      setSignupStep('success');
      setAuthSuccess('');
      setProfileFieldErrors({});
      setProfileErrorFocusKey(null);
    } catch (err) {
      setHideSignupEmailVerifyBanner(true);
      setAuthError(err.message || 'Unable to save profile.');
    } finally {
      setAuthLoading(false);
    }
  };

  const profileAge = profileForm.date_of_birth
    ? ageFromIsoDateInput(profileForm.date_of_birth)
    : null;
  const guardianRequired = profileAge != null && profileAge < 18;

  const signupProfileFieldClass = (key) =>
    profileFieldErrors[key] ? 'auth-form__control--invalid' : undefined;

  const renderSignupProfileFieldError = (key) => {
    const msg = profileFieldErrors[key];
    if (!msg) return null;
    const fid = SIGNUP_PROFILE_FIELD_IDS[key];
    return (
      <p id={fid ? `${fid}-err` : undefined} className="auth-form__field-error" role="alert">
        {msg}
      </p>
    );
  };

  const signupCredentialsFieldClass = (key) =>
    credentialsFieldErrors[key] ? 'auth-form__control--invalid' : undefined;

  const renderSignupCredentialsFieldError = (key) => {
    const msg = credentialsFieldErrors[key];
    if (!msg) return null;
    const fid = SIGNUP_CREDENTIALS_FIELD_IDS[key];
    return (
      <p id={fid ? `${fid}-err` : undefined} className="auth-form__field-error" role="alert">
        {msg}
      </p>
    );
  };

  return (
    <>
      {/* Top Bar */}
      <div className={`topbar${scrolled ? ' topbar--hidden' : ''}`}>
        <div className="topbar__container">
          <div className="topbar__left">
            <a href="tel:+918143231857" className="topbar__item">
              <FaPhone />
              <span>+91 81432 31857</span>
            </a>
            <span className="topbar__divider" />
            <span className="topbar__item topbar__item--address">
              <FaMapMarkerAlt />
              <span>HIG-137, Mayurinagar, Miyapur, Hyderabad</span>
            </span>
          </div>
          <div className="topbar__right">
            <div className="topbar__social">
              <a
                href="https://www.youtube.com/@Digirasa"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
              >
                <FaYoutube />
              </a>
              <a
                href="https://www.instagram.com/p/DE1mGs3JH_S/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <FaInstagram />
              </a>
              <a
                href="https://www.facebook.com/people/DigiRasa/61574968622139/#"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <FaFacebookF />
              </a>
              <a
                href="https://in.pinterest.com/digirasa/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Pinterest"
              >
                <FaPinterestP />
              </a>
            </div>
            <span className="topbar__divider" />
            <div className="topbar__applinks">
              <a
                href="https://play.google.com/store/apps/details?id=com.digirasa.client"
                target="_blank"
                rel="noopener noreferrer"
                className="topbar__app-btn topbar__app-btn--play"
                aria-label="Get it on Google Play"
              >
                <SiGoogleplay />
                <div className="topbar__app-btn__text">
                  <span className="topbar__app-btn__sub">GET IT ON</span>
                  <span className="topbar__app-btn__name">Google Play</span>
                </div>
              </a>
              <a
                href="https://apps.apple.com/in/app/digirasa/id6736368836"
                target="_blank"
                rel="noopener noreferrer"
                className="topbar__app-btn topbar__app-btn--apple"
                aria-label="Download on the App Store"
              >
                <SiAppstore />
                <div className="topbar__app-btn__text">
                  <span className="topbar__app-btn__sub">Download on the</span>
                  <span className="topbar__app-btn__name">App Store</span>
                </div>
              </a>
            </div>
            <div className="topbar__account-wrap" ref={topbarAccountRef}>
              <button
                type="button"
                className="topbar__auth-btn"
                onClick={() =>
                  authToken
                    ? setAccountDropdown((v) => (v === 'topbar' ? null : 'topbar'))
                    : openAuthModal()
                }
                aria-expanded={authToken ? accountDropdown === 'topbar' : undefined}
                aria-haspopup={authToken ? 'menu' : undefined}
              >
                <FaUser />
                <span>
                  {authToken ? `Hi, ${authUser?.name || 'My Account'}` : 'Login / Sign Up'}
                </span>
                {authToken ? <FaChevronDown className="topbar__auth-chevron" aria-hidden /> : null}
              </button>
              {authToken && accountDropdown === 'topbar' && (
                <AccountDropdownMenu
                  className="topbar__account-dropdown"
                  onClose={() => setAccountDropdown(null)}
                  onLogout={handleLogout}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <header className={`navbar${scrolled ? ' navbar--scrolled' : ''}`}>
        <div className="navbar__container">
          <div className="navbar__brand">
            <Link to="/" className="navbar__logo" onClick={() => setMenuOpen(false)}>
              <img src={logo} alt="Digi Rasa" />
            </Link>

            <KitchenClosedBanner />

            <div className="navbar__veg-badge" aria-label="100 percent vegetarian">
              <FaLeaf />
              <span>100% Vegetarian</span>
            </div>
          </div>

          <nav className={`navbar__nav${menuOpen ? ' navbar__nav--open' : ''}`}>
            {/* Navigation items */}
            {navItems.map((item) => {
              if (item.hasDropdown) {
                const isOpen = item.label === 'ABOUT' ? aboutDropdownOpen : supportDropdownOpen;
                const setOpen =
                  item.label === 'ABOUT' ? setAboutDropdownOpen : setSupportDropdownOpen;
                const ref = item.label === 'ABOUT' ? aboutRef : supportRef;
                const isActive = item.dropdown.some((d) => location.pathname === d.path);
                return (
                  <div
                    key={item.label}
                    className={`navbar__dropdown${isOpen ? ' navbar__dropdown--open' : ''}`}
                    ref={ref}
                  >
                    <button
                      className={`navbar__link navbar__dropdown-trigger${isActive ? ' navbar__link--active' : ''}`}
                      onClick={() => setOpen(!isOpen)}
                      aria-expanded={isOpen}
                    >
                      {item.label} <FaChevronDown className="navbar__dropdown-arrow" />
                    </button>
                    <div className="navbar__dropdown-menu">
                      {item.dropdown.map((sub) => (
                        <NavLink
                          key={sub.path}
                          to={sub.path}
                          className={({ isActive }) =>
                            `navbar__dropdown-item${isActive ? ' navbar__dropdown-item--active' : ''}`
                          }
                          onClick={() => {
                            setOpen(false);
                            setMenuOpen(false);
                          }}
                        >
                          {sub.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                );
              } else {
                return (
                  <NavLink
                    key={item.label}
                    to={item.path}
                    end={item.path === '/'}
                    className={({ isActive: linkActive }) =>
                      `navbar__link${linkActive ? ' navbar__link--active' : ''}`
                    }
                    onClick={() => setMenuOpen(false)}
                  >
                    {item.label}
                  </NavLink>
                );
              }
            })}
          </nav>

          <div className="navbar__actions">
            <button
              className="navbar__icon-btn"
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
            >
              <FaSearch />
            </button>
            <Link
              to="/subscription-cart"
              className="navbar__icon-btn navbar__cart-btn"
              aria-label={
                subscriptionCartCount > 0
                  ? `Subscription cart, ${subscriptionCartCount} items`
                  : 'Subscription cart'
              }
              onClick={() => setMenuOpen(false)}
            >
              <FaCalendarAlt />
              {subscriptionCartCount > 0 ? (
                <span className="navbar__cart-badge" aria-hidden>
                  {subscriptionCartCount > 99 ? '99+' : subscriptionCartCount}
                </span>
              ) : null}
            </Link>
            <Link
              to="/cart"
              className="navbar__icon-btn navbar__cart-btn"
              aria-label={cartCount > 0 ? `Cart, ${cartCount} items` : 'Cart'}
              onClick={() => setMenuOpen(false)}
            >
              <FaShoppingCart />
              {cartCount > 0 ? (
                <span className="navbar__cart-badge" aria-hidden>
                  {cartCount > 99 ? '99+' : cartCount}
                </span>
              ) : null}
            </Link>
            <div className="navbar__account-wrap" ref={navbarAccountRef}>
              <button
                type="button"
                className="navbar__icon-btn"
                onClick={() => {
                  setMenuOpen(false);
                  if (authToken) {
                    setAccountDropdown((v) => (v === 'navbar' ? null : 'navbar'));
                  } else {
                    openAuthModal();
                  }
                }}
                aria-label="Account"
                aria-expanded={authToken ? accountDropdown === 'navbar' : undefined}
                aria-haspopup={authToken ? 'menu' : undefined}
              >
                <FaUser />
              </button>
              {authToken && accountDropdown === 'navbar' && (
                <AccountDropdownMenu
                  className="navbar__account-menu account-dropdown"
                  onClose={() => {
                    setAccountDropdown(null);
                    setMenuOpen(false);
                  }}
                  onLogout={handleLogout}
                />
              )}
            </div>
            <button
              className="navbar__toggle"
              onClick={() => {
                setAccountDropdown(null);
                setMenuOpen((open) => !open);
              }}
              aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={menuOpen}
              type="button"
            >
              {menuOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>
      </header>

      {/* Search Overlay */}
      {searchOpen && (
        <div className="search-overlay" onClick={() => setSearchOpen(false)}>
          <div className="search-overlay__box" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={handleSearchSubmit}>
              <FaSearch className="search-overlay__icon" />
              <input
                ref={searchRef}
                type="text"
                placeholder="Search for dishes, categories…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button type="submit" className="search-overlay__submit">
                Search
              </button>
            </form>
            <button
              className="search-overlay__close"
              onClick={() => setSearchOpen(false)}
              aria-label="Close"
            >
              <FaTimes />
            </button>
          </div>
        </div>
      )}

      {/* Auth modal: login / sign-up only (signed-in users use account dropdown + /account pages) */}
      {authOpen && !authToken && (
        <div className="auth-overlay" role="presentation">
          <div className="auth-modal">
            <button
              className="auth-modal__close"
              onClick={() => setAuthOpen(false)}
              aria-label="Close"
            >
              <FaTimes />
            </button>
            <div className="auth-modal__logo">
              <img src={logo} alt="DigiRasa" />
            </div>
            <div className="auth-modal__tabs">
              <button
                type="button"
                className={`auth-tab${authMode === 'login' ? ' auth-tab--active' : ''}`}
                onClick={() => {
                  setAuthMode('login');
                  setAuthError('');
                  setAuthSuccess('');
                  resetSignupWizard();
                  resetLoginIdentifier();
                }}
              >
                Login
              </button>
              <button
                type="button"
                className={`auth-tab${authMode === 'signup' ? ' auth-tab--active' : ''}`}
                onClick={() => {
                  setAuthMode('signup');
                  setAuthError('');
                  setAuthSuccess('');
                  resetSignupWizard();
                  resetLoginIdentifier();
                }}
              >
                Sign Up
              </button>
            </div>

            <>
              {authError && (
                <div className="auth-inline-message auth-inline-message--error">{authError}</div>
              )}
              {authSuccess && (
                <div className="auth-inline-message auth-inline-message--success">
                  {authSuccess}
                </div>
              )}

              {authMode === 'login' ? (
                <form className="auth-form" onSubmit={handleLoginSubmit} noValidate>
                  <div className="auth-form__group">
                    <span className="auth-form__step-label" id="web-login-mode-label">
                      Sign in with
                    </span>
                    <div
                      className="auth-login-mode"
                      role="radiogroup"
                      aria-labelledby="web-login-mode-label"
                    >
                      <label className="auth-login-mode__radio">
                        <input
                          type="radio"
                          name="loginIdentifierMode"
                          value="phone"
                          checked={loginIdentifierMode === 'phone'}
                          onChange={() => setLoginIdentifierMode('phone')}
                        />
                        <span>Mobile</span>
                      </label>
                      <label className="auth-login-mode__radio">
                        <input
                          type="radio"
                          name="loginIdentifierMode"
                          value="account"
                          checked={loginIdentifierMode === 'account'}
                          onChange={() => setLoginIdentifierMode('account')}
                        />
                        <span>Username or email</span>
                      </label>
                    </div>
                  </div>
                  <div id="web-login-identifier-panel">
                  {loginIdentifierMode === 'phone' ? (
                    <div className="auth-form__group">
                      <label htmlFor="web-login-mobile">Mobile Number *</label>
                      <div className="auth-phone-row">
                        <select
                          id="web-login-country-code"
                          name="countryCode"
                          value={authForm.countryCode}
                          onChange={handleAuthInput}
                          aria-label="Country code"
                        >
                          {COUNTRY_CODES.map((code) => (
                            <option key={code} value={code}>
                              +{code}
                            </option>
                          ))}
                        </select>
                        <input
                          id="web-login-mobile"
                          name="mobile"
                          type="tel"
                          inputMode="numeric"
                          value={authForm.mobile}
                          onChange={handleAuthInput}
                          placeholder={`${getLocalMobileLength(authForm.countryCode)}-digit mobile`}
                          maxLength={getLocalMobileLength(authForm.countryCode)}
                          autoComplete="tel-national"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="auth-form__group">
                      <label htmlFor="web-login-account">Username or email *</label>
                      <input
                        id="web-login-account"
                        name="loginAccount"
                        type="text"
                        value={authForm.loginAccount}
                        onChange={handleAuthInput}
                        placeholder="you@example.com or your_username"
                        autoComplete="username"
                      />
                    </div>
                  )}
                  </div>
                  <div className="auth-form__group">
                    <label htmlFor="web-login-password">Password *</label>
                    <PasswordField
                      id="web-login-password"
                      name="password"
                      value={authForm.password}
                      onChange={handleAuthInput}
                      placeholder="Enter your password"
                      autoComplete="current-password"
                      disabled={authLoading}
                    />
                  </div>
                  <button
                    type="button"
                    className="auth-form__forgot"
                    onClick={() => {
                      setAuthOpen(false);
                      navigate('/account/forgot-password');
                    }}
                  >
                    Forgot password?
                  </button>
                  <button type="submit" className="auth-form__submit" disabled={authLoading}>
                    {authLoading ? 'Logging in...' : 'Login'}
                  </button>
                </form>
              ) : signupStep === 'phone' ? (
                <form className="auth-form" onSubmit={handleSignupPhoneContinue} noValidate>
                  <p className="auth-form__step-label">Step 1 of 3 — Mobile number</p>
                  <p className="auth-form__hint">
                    Enter your mobile number. We will send a one-time code to verify it before you
                    create your account.
                  </p>
                  <div className="auth-form__group">
                    <label htmlFor="web-signup-mobile">Mobile Number *</label>
                    <div className="auth-phone-row">
                      <select
                        id="web-signup-country-code"
                        name="countryCode"
                        value={authForm.countryCode}
                        onChange={handleAuthInput}
                        aria-label="Country code"
                      >
                        {COUNTRY_CODES.map((code) => (
                          <option key={code} value={code}>
                            +{code}
                          </option>
                        ))}
                      </select>
                      <input
                        id="web-signup-mobile"
                        name="mobile"
                        type="tel"
                        inputMode="numeric"
                        value={authForm.mobile}
                        onChange={handleAuthInput}
                        placeholder={`${getLocalMobileLength(authForm.countryCode)}-digit mobile`}
                        maxLength={getLocalMobileLength(authForm.countryCode)}
                        autoComplete="tel-national"
                      />
                    </div>
                  </div>
                  <button type="submit" className="auth-form__submit" disabled={authLoading}>
                    {authLoading ? 'Sending OTP...' : 'Send OTP'}
                  </button>
                </form>
              ) : signupStep === 'otp' ? (
                <form className="auth-form" onSubmit={handleSignupOtpSubmit} noValidate>
                  <p className="auth-form__step-label">Step 1 of 3 — Verify mobile</p>
                  <p className="auth-form__hint">
                    Code sent to{' '}
                    {formatPhoneForDisplay(
                      formatPhoneForRegister(authForm.countryCode, authForm.mobile),
                    )}
                    . Valid for{' '}
                    <strong>{formatCountdown(otpSecondsLeft)}</strong>
                    {otpSecondsLeft === 0 ? ' (request a new code)' : ''}.
                  </p>
                  <div className="auth-form__group">
                    <label htmlFor="web-signup-otp">6-digit OTP *</label>
                    <input
                      id="web-signup-otp"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      value={signupOtp}
                      onChange={(ev) =>
                        setSignupOtp(ev.target.value.replace(/\D/g, '').slice(0, 6))
                      }
                      placeholder="••••••"
                    />
                  </div>
                  <button
                    type="submit"
                    className="auth-form__submit"
                    disabled={authLoading || otpSecondsLeft === 0}
                  >
                    {authLoading ? 'Verifying...' : 'Verify & continue'}
                  </button>
                  <div className="auth-form__row-actions">
                    <button
                      type="button"
                      className="auth-form__secondary-btn"
                      disabled={resendCooldownSec > 0 || authLoading}
                      onClick={handleSignupResendOtp}
                    >
                      {resendCooldownSec > 0 ? `Resend in ${resendCooldownSec}s` : 'Resend OTP'}
                    </button>
                    <button
                      type="button"
                      className="auth-form__text-btn"
                      onClick={() => {
                        setSignupStep('phone');
                        setSignupVerificationId('');
                        setSignupOtp('');
                        setOtpSecondsLeft(0);
                        setResendCooldownSec(0);
                        setAuthError('');
                        setAuthSuccess('');
                      }}
                    >
                      Change mobile number
                    </button>
                  </div>
                </form>
              ) : signupStep === 'credentials' ? (
                <form className="auth-form" onSubmit={handleSignupCredentialsSubmit} noValidate>
                  <p className="auth-form__step-label">Step 2 of 3 — Account details</p>
                  <p className="auth-form__hint">
                    Mobile verified
                    {pendingSignup?.phone_number
                      ? ` (${formatPhoneForDisplay(pendingSignup.phone_number)})`
                      : ''}
                    . Choose
                    your username, email, and password.
                  </p>
                  <div className="auth-form__group">
                    <label htmlFor="web-signup-username">Username *</label>
                    <input
                      id="web-signup-username"
                      name="username"
                      type="text"
                      value={authForm.username}
                      onChange={handleAuthInput}
                      placeholder="Choose a username"
                      autoComplete="username"
                      aria-invalid={!!credentialsFieldErrors.username}
                      aria-describedby={
                        credentialsFieldErrors.username ? 'web-signup-username-err' : undefined
                      }
                      className={signupCredentialsFieldClass('username')}
                    />
                    {renderSignupCredentialsFieldError('username')}
                  </div>
                  <div className="auth-form__group">
                    <label htmlFor="web-signup-email">Email *</label>
                    <input
                      id="web-signup-email"
                      name="email"
                      type="email"
                      value={authForm.email}
                      onChange={handleAuthInput}
                      placeholder="you@example.com"
                      autoComplete="email"
                      aria-invalid={!!credentialsFieldErrors.email}
                      aria-describedby={
                        credentialsFieldErrors.email ? 'web-signup-email-err' : undefined
                      }
                      className={signupCredentialsFieldClass('email')}
                    />
                    {renderSignupCredentialsFieldError('email')}
                  </div>
                  <div className="auth-form__group">
                    <label htmlFor="web-signup-password">Password *</label>
                    <PasswordField
                      id="web-signup-password"
                      name="password"
                      value={authForm.password}
                      onChange={handleAuthInput}
                      placeholder="At least 8 characters"
                      autoComplete="new-password"
                      disabled={authLoading}
                      aria-invalid={!!credentialsFieldErrors.password}
                      aria-describedby={
                        credentialsFieldErrors.password
                          ? 'web-signup-password-err'
                          : 'web-signup-password-hint'
                      }
                      className={signupCredentialsFieldClass('password')}
                    />
                    {!credentialsFieldErrors.password ? (
                      <p id="web-signup-password-hint" className="auth-form__hint">
                        Use 8+ characters with letters, numbers, and a special character. No spaces.
                      </p>
                    ) : null}
                    {renderSignupCredentialsFieldError('password')}
                  </div>
                  <div className="auth-form__group">
                    <label htmlFor="web-signup-confirm-password">Confirm password *</label>
                    <PasswordField
                      id="web-signup-confirm-password"
                      name="confirmPassword"
                      value={authForm.confirmPassword}
                      onChange={handleAuthInput}
                      placeholder="Re-enter your password"
                      autoComplete="new-password"
                      disabled={authLoading}
                      aria-invalid={!!credentialsFieldErrors.confirmPassword}
                      aria-describedby={
                        credentialsFieldErrors.confirmPassword
                          ? 'web-signup-confirm-password-err'
                          : undefined
                      }
                      className={signupCredentialsFieldClass('confirmPassword')}
                    />
                    {renderSignupCredentialsFieldError('confirmPassword')}
                  </div>
                  <button type="submit" className="auth-form__submit" disabled={authLoading}>
                    {authLoading ? 'Creating account...' : 'Continue'}
                  </button>
                </form>
              ) : signupStep === 'profile' ? (
                <form className="auth-form" onSubmit={handleProfileSubmit} noValidate>
                  <p className="auth-form__step-label">Step 3 of 3 — Personal details</p>
                  {!hideSignupEmailVerifyBanner ? (
                    <div
                      className="auth-inline-message auth-inline-message--success"
                      role="status"
                      style={{ marginBottom: '12px' }}
                    >
                      <strong>Verify your email</strong>
                      <p className="auth-form__hint" style={{ margin: '8px 0 0' }}>
                        Please click on the link in the email we just sent
                        {pendingSignup?.email
                          ? ` to ${pendingSignup.email}`
                          : ' you'}{' '}
                        to confirm your email address.
                      </p>
                    </div>
                  ) : null}
                  <p className="auth-form__hint">
                    Optional — add your personal and guardian details now, or skip and complete them
                    later from My Profile.
                  </p>
                  <div className="auth-form__two-col">
                    <div className="auth-form__group">
                      <label htmlFor="web-profile-fn">First name *</label>
                      <input
                        id="web-profile-fn"
                        name="first_name"
                        type="text"
                        value={profileForm.first_name}
                        onChange={handleProfileInput}
                        autoComplete="given-name"
                        aria-invalid={!!profileFieldErrors.first_name}
                        aria-describedby={
                          profileFieldErrors.first_name ? 'web-profile-fn-err' : undefined
                        }
                        className={signupProfileFieldClass('first_name')}
                      />
                      {renderSignupProfileFieldError('first_name')}
                    </div>
                    <div className="auth-form__group">
                      <label htmlFor="web-profile-ln">Last name *</label>
                      <input
                        id="web-profile-ln"
                        name="last_name"
                        type="text"
                        value={profileForm.last_name}
                        onChange={handleProfileInput}
                        autoComplete="family-name"
                        aria-invalid={!!profileFieldErrors.last_name}
                        aria-describedby={
                          profileFieldErrors.last_name ? 'web-profile-ln-err' : undefined
                        }
                        className={signupProfileFieldClass('last_name')}
                      />
                      {renderSignupProfileFieldError('last_name')}
                    </div>
                  </div>
                  <div className="auth-form__group">
                    <label htmlFor="web-profile-gender">Gender *</label>
                    <select
                      id="web-profile-gender"
                      name="gender"
                      value={profileForm.gender}
                      onChange={handleProfileInput}
                      aria-invalid={!!profileFieldErrors.gender}
                      aria-describedby={
                        profileFieldErrors.gender ? 'web-profile-gender-err' : undefined
                      }
                      className={signupProfileFieldClass('gender')}
                    >
                      <option value="">Select</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                    {renderSignupProfileFieldError('gender')}
                  </div>
                  <div className="auth-form__group">
                    <label id="web-profile-dob-label" htmlFor="web-profile-dob-day">
                      Date of birth *
                    </label>
                    <div
                      className="auth-form__dob-row"
                      role="group"
                      aria-labelledby="web-profile-dob-label"
                    >
                      <select
                        id="web-profile-dob-day"
                        aria-label="Day"
                        value={dobParts.day}
                        onChange={(e) => handleDobPartChange('day', e.target.value)}
                        aria-invalid={!!profileFieldErrors.date_of_birth}
                        aria-describedby={
                          profileFieldErrors.date_of_birth
                            ? 'web-profile-dob-err'
                            : 'web-profile-dob-hint'
                        }
                        className={signupProfileFieldClass('date_of_birth')}
                      >
                        <option value="">DD</option>
                        {Array.from(
                          { length: daysInMonth(dobParts.year, dobParts.month) },
                          (_, i) => {
                            const day = String(i + 1).padStart(2, '0');
                            return (
                              <option key={day} value={day}>
                                {day}
                              </option>
                            );
                          },
                        )}
                      </select>
                      <select
                        id="web-profile-dob-month"
                        aria-label="Month"
                        value={dobParts.month}
                        onChange={(e) => handleDobPartChange('month', e.target.value)}
                        aria-invalid={!!profileFieldErrors.date_of_birth}
                        className={signupProfileFieldClass('date_of_birth')}
                      >
                        <option value="">MM</option>
                        {MONTH_OPTIONS.map((month) => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                      <select
                        id="web-profile-dob-year"
                        aria-label="Year"
                        value={dobParts.year}
                        onChange={(e) => handleDobPartChange('year', e.target.value)}
                        aria-invalid={!!profileFieldErrors.date_of_birth}
                        className={signupProfileFieldClass('date_of_birth')}
                      >
                        <option value="">YYYY</option>
                        {DOB_YEAR_OPTIONS.map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>
                    {!profileFieldErrors.date_of_birth ? (
                      <p id="web-profile-dob-hint" className="auth-form__hint">
                        Select DD / MM / YYYY. Under 18 requires guardian details below.
                      </p>
                    ) : null}
                    {renderSignupProfileFieldError('date_of_birth')}
                  </div>
                  <div className="auth-form__group">
                    <label htmlFor="web-profile-city">City *</label>
                    <input
                      id="web-profile-city"
                      name="city"
                      type="text"
                      value={profileForm.city}
                      onChange={handleProfileInput}
                      autoComplete="address-level2"
                      aria-invalid={!!profileFieldErrors.city}
                      aria-describedby={
                        profileFieldErrors.city ? 'web-profile-city-err' : undefined
                      }
                      className={signupProfileFieldClass('city')}
                    />
                    {renderSignupProfileFieldError('city')}
                  </div>
                  <div className="auth-form__group">
                    <label htmlFor="web-profile-state">State *</label>
                    <select
                      id="web-profile-state"
                      name="state"
                      value={profileForm.state}
                      onChange={handleProfileInput}
                      aria-invalid={!!profileFieldErrors.state}
                      aria-describedby={
                        profileFieldErrors.state ? 'web-profile-state-err' : undefined
                      }
                      className={signupProfileFieldClass('state')}
                    >
                      <option value="">Select state</option>
                      {INDIAN_STATES.map((st) => (
                        <option key={st} value={st}>
                          {st}
                        </option>
                      ))}
                    </select>
                    {renderSignupProfileFieldError('state')}
                  </div>
                  <div className="auth-form__group">
                    <label htmlFor="web-profile-country">Country</label>
                    <input
                      id="web-profile-country"
                      name="country"
                      type="text"
                      value={profileForm.country}
                      onChange={handleProfileInput}
                      autoComplete="country-name"
                      aria-invalid={!!profileFieldErrors.country}
                      className={signupProfileFieldClass('country')}
                    />
                    {renderSignupProfileFieldError('country')}
                  </div>
                  <div className="auth-form__group">
                    <label htmlFor="web-profile-pin">Pin code *</label>
                    <input
                      id="web-profile-pin"
                      name="pincode"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={profileForm.pincode}
                      onChange={(ev) => {
                        setProfileForm((prev) => ({
                          ...prev,
                          pincode: ev.target.value.replace(/\D/g, '').slice(0, 6),
                        }));
                        setProfileFieldErrors((prev) => {
                          if (!prev.pincode) return prev;
                          const next = { ...prev };
                          delete next.pincode;
                          return next;
                        });
                        setProfileErrorFocusKey((prev) => (prev === 'pincode' ? null : prev));
                      }}
                      aria-invalid={!!profileFieldErrors.pincode}
                      aria-describedby={
                        profileFieldErrors.pincode ? 'web-profile-pin-err' : undefined
                      }
                      className={signupProfileFieldClass('pincode')}
                    />
                    {renderSignupProfileFieldError('pincode')}
                  </div>
                  <fieldset className="auth-form__fieldset">
                    <legend>{guardianRequired ? 'Guardian *' : 'Guardian (optional)'}</legend>
                    <div className="auth-form__group">
                      <label htmlFor="web-profile-g-name">
                        Guardian name{guardianRequired ? ' *' : ''}
                      </label>
                      <input
                        id="web-profile-g-name"
                        name="guardian_name"
                        type="text"
                        value={profileForm.guardian_name}
                        onChange={handleProfileInput}
                        required={guardianRequired}
                        aria-required={guardianRequired}
                        aria-invalid={!!profileFieldErrors.guardian_name}
                        aria-describedby={
                          profileFieldErrors.guardian_name ? 'web-profile-g-name-err' : undefined
                        }
                        className={signupProfileFieldClass('guardian_name')}
                      />
                      {renderSignupProfileFieldError('guardian_name')}
                    </div>
                    <div className="auth-form__group">
                      <label htmlFor="web-profile-g-email">
                        Guardian email{guardianRequired ? ' *' : ''}
                      </label>
                      <input
                        id="web-profile-g-email"
                        name="guardian_email"
                        type="email"
                        value={profileForm.guardian_email}
                        onChange={handleProfileInput}
                        required={guardianRequired}
                        aria-required={guardianRequired}
                        aria-invalid={!!profileFieldErrors.guardian_email}
                        aria-describedby={
                          profileFieldErrors.guardian_email ? 'web-profile-g-email-err' : undefined
                        }
                        className={signupProfileFieldClass('guardian_email')}
                      />
                      {renderSignupProfileFieldError('guardian_email')}
                    </div>
                    <div className="auth-form__group">
                      <label htmlFor="web-profile-g-mobile">
                        Guardian mobile{guardianRequired ? ' *' : ''}
                      </label>
                      <input
                        id="web-profile-g-mobile"
                        name="guardian_mobile"
                        type="tel"
                        inputMode="numeric"
                        placeholder="919999999999"
                        maxLength={15}
                        value={profileForm.guardian_mobile}
                        onChange={handleProfileInput}
                        required={guardianRequired}
                        aria-required={guardianRequired}
                        aria-invalid={!!profileFieldErrors.guardian_mobile}
                        aria-describedby={
                          profileFieldErrors.guardian_mobile
                            ? 'web-profile-g-mobile-err'
                            : undefined
                        }
                        className={signupProfileFieldClass('guardian_mobile')}
                      />
                      {renderSignupProfileFieldError('guardian_mobile')}
                    </div>
                  </fieldset>
                  <button type="submit" className="auth-form__submit" disabled={authLoading}>
                    {authLoading ? 'Saving...' : 'Save & continue'}
                  </button>
                  <button
                    type="button"
                    className="auth-form__secondary-btn"
                    style={{ marginTop: '10px', width: '100%' }}
                    disabled={authLoading}
                    onClick={skipSignupProfile}
                  >
                    Skip for now
                  </button>
                </form>
              ) : signupStep === 'success' ? (
                <div className="auth-form auth-signup-success">
                  <p className="auth-form__step-label">Registration successful</p>
                  <div
                    className="auth-inline-message auth-inline-message--success"
                    style={{ marginBottom: '12px' }}
                  >
                    Your DigiRasa account has been created successfully.
                  </div>
                  <div className="auth-signup-success__details" role="status">
                    <p>
                      <span>Username</span>
                      <strong>{pendingSignup?.username || authForm.username || '—'}</strong>
                    </p>
                    <p>
                      <span>Mobile</span>
                      <strong>
                        {formatPhoneForDisplay(pendingSignup?.phone_number) || '—'}
                      </strong>
                    </p>
                    <p>
                      <span>Email</span>
                      <strong>{pendingSignup?.email || authForm.email || '—'}</strong>
                    </p>
                  </div>
                  <button type="button" className="auth-form__submit" onClick={goToLoginAfterSignup}>
                    Go to Login
                  </button>
                </div>
              ) : null}
            </>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
